import * as React from 'react';
import { Accordion as AccordionPrimitive } from 'radix-ui';
import { cn } from '@/lib/core';
import { Icon } from '@/components/ui/icon';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn('border-t-[0.5px] border-[#4e4e4e]', className)}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

interface AccordionTriggerProps extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
  /** Extra classes merged onto the inner label span (font size/weight/tracking overrides). */
  labelClassName?: string;
}

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(({ className, labelClassName, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'group flex w-full items-center justify-between py-2 text-left',
        'data-disabled:cursor-not-allowed data-disabled:opacity-40',
        className
      )}
      {...props}
    >
      <span
        className={cn(
          'font-pp-neue-montreal min-w-0 flex-1 text-[12px] leading-none font-normal tracking-[0.5px] text-[#1a1a1a] uppercase group-data-[state=open]:font-semibold',
          labelClassName
        )}
      >
        {children}
      </span>
      <span className="flex size-11 shrink-0 items-center justify-center">
        <Icon
          name="nav-arrow-down"
          width={20}
          height={20}
          className="text-[#1a1a1a] transition-transform duration-200 group-data-[state=open]:rotate-180"
        />
      </span>
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden"
    {...props}
  >
    <div className={cn(className)}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
